import { Box, FormControl, FormLabel, Grid, OutlinedInput, Button, FormHelperText, Stack, Alert, Divider, Tooltip, Paper, Typography, IconButton, Collapse, Dialog, DialogContent, DialogTitle } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useRef, useState, useMemo } from "react";
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { getReportRequest, postReportRequest, putReportRequest, resetGetReport } from '../../../../redux/modules/report/slice';
import { useDispatch, useSelector } from 'react-redux';
import SelectStaff from '../form/SelectStaff';
import { getStaffList } from '../../../../redux/modules/staff/slice';
import DictationTextField from '../../../../components/DictationTextField';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useReportMode } from '../../../../hooks/useSidebar';
import TemplateSelector from '../../../../components/TemplateSelector';
import { OBMeasurementOverlay } from '../../../../components/OBMeasurements';

const validationSchema = yup.object().shape({
    diagnosticObjectives: yup.string(),
    findings: yup.string(),
    radiographicImpression: yup.string(),
    dcmFiles: yup.mixed(),
    appointerId: yup.string(),
    appointerSignaturePin: yup.string(),
    reportStatus: yup.string(),
});

export default function DiagnosisRadiologyReport({ itemData, onBack, setSaveType, onReportSubmitted }) {
    const dispatch = useDispatch();
    let { currentBranch } = useSelector(state => state.branch);
    let { loading, getReportRes } = useSelector(state => state.report);
    const formRef = useRef(null);
    const [obMeasurements, setObMeasurements] = useState(null);
    const [showOBAlert, setShowOBAlert] = useState(false);
    const [worksheetExpanded, setWorksheetExpanded] = useState(true);
    const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
    const [pdfDialogUrl, setPdfDialogUrl] = useState('');
    const [pdfDialogTitle, setPdfDialogTitle] = useState('');
    const [pdfDialogType, setPdfDialogType] = useState('pdf');
    const [pdfDialogInstanceId, setPdfDialogInstanceId] = useState('');
    const [embeddedPdfUrl, setEmbeddedPdfUrl] = useState('');
    const { enterReportMode, reportMode } = useReportMode();

    // Check if study has images for Report Mode
    const hasStudyImages = !!(itemData?.studyData?.orthancStudyInstanceUID);

    // Get encapsulated PDFs from order/study data
    const encapsulatedPDFs = useMemo(() => {
        return itemData?.studyData?.encapsulatedPDFs ||
               itemData?.orderData?.encapsulatedPDFs ||
               [];
    }, [itemData]);

    // Get DICOM comments from order/study data
    const dicomComments = useMemo(() => {
        return itemData?.studyData?.dicomComments ||
               itemData?.orderData?.dicomComments ||
               null;
    }, [itemData]);

    // Get structured reports
    const structuredReports = useMemo(() => {
        return itemData?.studyData?.structuredReports ||
               itemData?.orderData?.structuredReports ||
               [];
    }, [itemData]);

    // Get secondary capture instances (measurement images)
    const secondaryCaptureInstances = useMemo(() => {
        return itemData?.studyData?.secondaryCaptureInstances ||
               itemData?.orderData?.secondaryCaptureInstances ||
               [];
    }, [itemData]);

    const hasPdfs = encapsulatedPDFs.length > 0;
    const hasComments = dicomComments?.studyComments || dicomComments?.imageComments?.length > 0;
    const hasSRs = structuredReports.length > 0;
    const hasSCs = secondaryCaptureInstances.length > 0;
    const hasAnyWorksheetContent = hasPdfs || hasComments || hasSRs || hasSCs;

    // Detect if this is an OB exam based on exam names
    const isOBExam = useMemo(() => {
        const examNames = itemData?.examList?.flatMap(e => e.list?.map(l => l?.name?.toLowerCase()) || []) || [];
        const obKeywords = ['ob', 'obstetric', 'fetal', 'pregnancy', 'prenatal', 'anatomy scan', 'biometry', 'growth'];
        return examNames.some(name => name && obKeywords.some(keyword => name.includes(keyword)));
    }, [itemData?.examList]);

    // Server API URL for authenticated requests
    const serverApiUrl = process.env.REACT_APP_SERVER_API || 'http://localhost:9000/';

    // Get PDF URL for viewing/downloading
    const getPdfUrl = (instanceId) => {
        return `${serverApiUrl}api/dicom/orthanc/instances/${instanceId}/pdf`;
    };

    // Get image preview URL for secondary captures
    const getPreviewUrl = (instanceId) => {
        return `${serverApiUrl}api/dicom/orthanc/instances/${instanceId}/rendered`;
    };

    const getAuthHeaders = () => {
        const authToken = localStorage.getItem('item') || localStorage.getItem('token');
        const headers = {};
        if (authToken) {
            headers.Authorization = authToken;
        }

        const currentBranchRaw = localStorage.getItem('currentBranch');
        if (currentBranchRaw) {
            try {
                const currentBranchData = JSON.parse(currentBranchRaw);
                if (currentBranchData?.id) {
                    headers['X-Current-Branch'] = currentBranchData.id;
                }
            } catch (e) {
                // Ignore malformed branch metadata
            }
        }

        return headers;
    };

    // Fetch SC images with auth and cache as blob URLs
    const [scImageUrls, setScImageUrls] = useState({});
    useEffect(() => {
        if (!hasSCs) return;
        const headers = getAuthHeaders();
        if (!headers.Authorization) return;
        let cancelled = false;
        let previousUrls = [];
        const objectUrls = [];
        const loadImages = async () => {
            const urls = {};
            for (const sc of secondaryCaptureInstances) {
                if (!sc.instanceId) continue;
                try {
                    const res = await fetch(getPreviewUrl(sc.instanceId), {
                        headers
                    });
                    if (!res.ok) continue;
                    const blob = await res.blob();
                    if (cancelled) return;
                    const objectUrl = URL.createObjectURL(blob);
                    objectUrls.push(objectUrl);
                    urls[sc.instanceId] = objectUrl;
                } catch (e) {
                    // skip failed images
                }
            }
            if (!cancelled) {
                setScImageUrls((prevUrls) => {
                    previousUrls = Object.values(prevUrls);
                    return urls;
                });
            }
        };
        loadImages();
        return () => {
            cancelled = true;
            previousUrls.forEach((url) => URL.revokeObjectURL(url));
            objectUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [secondaryCaptureInstances, hasSCs]);

    // Handle PDF download
    const handleDownloadPdf = async (instanceId, title) => {
        try {
            const response = await fetch(getPdfUrl(instanceId), {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to download PDF');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${title || 'worksheet'}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('PDF download error:', error);
        }
    };

    // Open PDF in popup dialog
    const handleOpenPdfDialog = async (instanceId, title) => {
        try {
            const response = await fetch(getPdfUrl(instanceId), {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to load PDF');
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            setPdfDialogUrl(blobUrl);
            setPdfDialogType('pdf');
            setPdfDialogInstanceId(instanceId || '');
            setPdfDialogTitle(title || 'Sonographer Worksheet');
            setPdfDialogOpen(true);
        } catch (error) {
            console.error('PDF open error:', error);
        }
    };

    // Handle PDF print
    const handlePrintPdf = async (instanceId) => {
        try {
            const response = await fetch(getPdfUrl(instanceId), {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to load PDF');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const printWindow = window.open(url, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            }
        } catch (error) {
            console.error('PDF print error:', error);
        }
    };

    const handleOpenScDialog = async (instanceId, title) => {
        if (!instanceId) return;

        if (scImageUrls[instanceId]) {
            setPdfDialogUrl(scImageUrls[instanceId]);
            setPdfDialogType('image');
            setPdfDialogInstanceId('');
            setPdfDialogTitle(title || 'Measurement Image');
            setPdfDialogOpen(true);
            return;
        }

        try {
            const response = await fetch(getPreviewUrl(instanceId), {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to load measurement image');
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            setPdfDialogUrl(blobUrl);
            setPdfDialogType('image');
            setPdfDialogInstanceId('');
            setPdfDialogTitle(title || 'Measurement Image');
            setPdfDialogOpen(true);
        } catch (error) {
            console.error('SC image open error:', error);
        }
    };

    useEffect(() => {
        let cancelled = false;
        let urlToRevoke = null;
        const firstPdfInstanceId = encapsulatedPDFs[0]?.instanceId;

        if (!firstPdfInstanceId) {
            setEmbeddedPdfUrl('');
            return undefined;
        }

        const loadEmbeddedPdf = async () => {
            try {
                const response = await fetch(getPdfUrl(firstPdfInstanceId), {
                    headers: getAuthHeaders()
                });
                if (!response.ok) throw new Error('Failed to load embedded PDF');
                const blob = await response.blob();
                if (cancelled) return;
                urlToRevoke = URL.createObjectURL(blob);
                setEmbeddedPdfUrl(urlToRevoke);
            } catch (error) {
                if (!cancelled) {
                    setEmbeddedPdfUrl('');
                }
            }
        };

        loadEmbeddedPdf();

        return () => {
            cancelled = true;
            if (urlToRevoke) {
                URL.revokeObjectURL(urlToRevoke);
            }
        };
    }, [encapsulatedPDFs]);

    // Check for OB measurements from previous step
    useEffect(() => {
        const storedOB = sessionStorage.getItem('obMeasurements');
        if (storedOB) {
            try {
                const parsed = JSON.parse(storedOB);
                setObMeasurements(parsed);
                setShowOBAlert(true);
            } catch (e) {
                console.error('Failed to parse OB measurements:', e);
            }
        }
    }, []);

    // Handle OB measurements being added to report
    const handleOBMeasurementsToReport = (reportText, data) => {
        setObMeasurements({ reportText, ...data });
        sessionStorage.setItem('obMeasurements', JSON.stringify({ reportText, ...data }));
        setShowOBAlert(true);
    };

    const insertOBMeasurements = () => {
        if (obMeasurements?.reportText) {
            const currentFindings = getValues('findings') || '';
            const newFindings = obMeasurements.reportText + (currentFindings ? '\n\n' + currentFindings : '');
            setValue('findings', newFindings);
            setShowOBAlert(false);
            sessionStorage.removeItem('obMeasurements');
        }
    };

    const { register, handleSubmit, formState: { errors }, setValue, getValues, trigger, control } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            reportStatus : itemData?.reportData?.reportStatus,
        },
    });

    useEffect(() => {
        if (!currentBranch) { return }
        if (itemData?.reportData) { dispatch(getReportRequest(itemData.reportData?._id)); }
        dispatch(getStaffList())
    }, [dispatch, currentBranch, itemData]);

    // Re-fetch report data when returning from Report Mode
    useEffect(() => {
        if (!reportMode && itemData?.reportData?._id && currentBranch) {
            dispatch(getReportRequest(itemData.reportData._id));
        }
    }, [reportMode, itemData?.reportData?._id, currentBranch, dispatch]);

    useEffect(() => {
        if (getReportRes ) {
            if (getReportRes?.success) {
                setValue("appointerId", getReportRes?.data?.appointerId);
                setValue("diagnosticObjectives", getReportRes?.data?.diagnosticObjectives);
                setValue("findings", getReportRes?.data?.findings);
                setValue("radiographicImpression", getReportRes?.data?.radiographicImpression);
            }
            dispatch(resetGetReport())
        }
    }, [dispatch, getReportRes, setValue, itemData])

    const onSubmit = (data) => {
        data.radiologyId = itemData?.radiologyId;
        if (itemData?.reportData?._id) {
            data.reportId = itemData?.reportData?._id;
            dispatch(putReportRequest(data));
        } else {
            dispatch(postReportRequest(data));
        }
    };

    // Handle entering Report Mode
    const handleEnterReportMode = () => {
        // Pass callback to navigate to summary after submit in report mode
        enterReportMode(itemData, onReportSubmitted);
    };

    // Handle template application
    const handleApplyTemplate = ({ clinicalNotes, findings, impression }) => {
        if (clinicalNotes) setValue('diagnosticObjectives', clinicalNotes);
        if (findings) setValue('findings', findings);
        if (impression) setValue('radiographicImpression', impression);
    };

    return (
        <>
            {/* Sonographer Worksheet Section - Collapsible at top */}
            {hasAnyWorksheetContent && (
                <Paper elevation={0} sx={{ mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                    {/* Header - Always visible */}
                    <Box
                        onClick={() => setWorksheetExpanded(!worksheetExpanded)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            bgcolor: 'grey.50',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'grey.100' }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight={600}>
                                Sonographer Worksheet
                            </Typography>
                            {hasPdfs && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    ({encapsulatedPDFs.length} PDF{encapsulatedPDFs.length > 1 ? 's' : ''})
                                </Typography>
                            )}
                            {hasSCs && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: hasPdfs ? 0 : 1 }}>
                                    ({secondaryCaptureInstances.length} measurement image{secondaryCaptureInstances.length > 1 ? 's' : ''})
                                </Typography>
                            )}
                        </Box>
                        <IconButton size="small">
                            {worksheetExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>

                    {/* Collapsible Content */}
                    <Collapse in={worksheetExpanded}>
                        <Box sx={{ p: 2, pt: 0 }}>
                            {/* PDF List and Viewer */}
                            {hasPdfs && (
                                <Box sx={{ mt: 2 }}>
                                    <Stack spacing={1} sx={{ mb: 2 }}>
                                        {encapsulatedPDFs.map((pdf, index) => (
                                            <Paper
                                                key={pdf.instanceId || index}
                                                elevation={0}
                                                sx={{
                                                    p: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    borderRadius: 1,
                                                    bgcolor: 'background.paper',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <Box
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', flex: 1 }}
                                                    onClick={() => handleOpenPdfDialog(pdf.instanceId, pdf.documentTitle || `Worksheet ${index + 1}`)}
                                                >
                                                    <PictureAsPdfIcon sx={{ color: 'error.main' }} />
                                                    <Typography variant="body2" fontWeight={500} sx={{ '&:hover': { color: 'primary.main' } }}>
                                                        {pdf.documentTitle || `Worksheet ${index + 1}`}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Open popup viewer">
                                                        <IconButton size="small" onClick={() => handleOpenPdfDialog(pdf.instanceId, pdf.documentTitle || `Worksheet ${index + 1}`)}>
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Download">
                                                        <IconButton size="small" onClick={() => handleDownloadPdf(pdf.instanceId, pdf.documentTitle)}>
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Print">
                                                        <IconButton size="small" onClick={() => handlePrintPdf(pdf.instanceId)}>
                                                            <PrintIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Paper>
                                        ))}
                                    </Stack>

                                    {/* PDF Embed Viewer */}
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            height: 500,
                                            bgcolor: 'grey.100',
                                        }}
                                    >
                                        {embeddedPdfUrl ? (
                                            <iframe
                                                src={embeddedPdfUrl}
                                                title="Sonographer Worksheet"
                                                width="100%"
                                                height="100%"
                                                style={{ border: 'none' }}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                                <Typography variant="body2" color="text.secondary">Unable to load PDF preview</Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                </Box>
                            )}

                            {/* DICOM Comments */}
                            {hasComments && (
                                <Box sx={{ mt: hasPdfs ? 2 : 0 }}>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        Sonographer Comments
                                    </Typography>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        {dicomComments.studyComments && (
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                                                {dicomComments.studyComments}
                                            </Typography>
                                        )}
                                        {dicomComments.imageComments?.map((comment, idx) => (
                                            <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                                                {comment}
                                            </Typography>
                                        ))}
                                        {dicomComments.performingPhysician && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                Sonographer: {dicomComments.performingPhysician}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Box>
                            )}

                            {/* Structured Reports */}
                            {hasSRs && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        Structured Reports ({structuredReports.length})
                                    </Typography>
                                    {structuredReports.map((sr, idx) => (
                                        <Paper key={idx} elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {sr.seriesDescription}
                                            </Typography>
                                            {sr.contentText && (
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1, color: 'text.secondary' }}>
                                                    {sr.contentText}
                                                </Typography>
                                            )}
                                        </Paper>
                                    ))}
                                </Box>
                            )}

                            {/* Secondary Capture Measurement Images */}
                            {hasSCs && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        Measurement Images ({secondaryCaptureInstances.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {secondaryCaptureInstances.map((sc, idx) => (
                                            <Paper
                                                key={sc.instanceId || idx}
                                                elevation={0}
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        boxShadow: 2,
                                                    },
                                                    width: 180,
                                                }}
                                                onClick={() => {
                                                    handleOpenScDialog(
                                                        sc.instanceId,
                                                        sc.imageComments || sc.seriesDescription || `Measurement ${idx + 1}`
                                                    );
                                                }}
                                            >
                                                {scImageUrls[sc.instanceId] ? (
                                                    <Box
                                                        component="img"
                                                        src={scImageUrls[sc.instanceId]}
                                                        alt={sc.imageComments || `Measurement ${idx + 1}`}
                                                        sx={{
                                                            width: '100%',
                                                            height: 135,
                                                            objectFit: 'cover',
                                                            bgcolor: 'grey.200',
                                                        }}
                                                    />
                                                ) : (
                                                    <Box sx={{ width: '100%', height: 135, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Typography variant="caption" color="text.secondary">Loading...</Typography>
                                                    </Box>
                                                )}
                                                {sc.imageComments && (
                                                    <Typography variant="caption" sx={{ p: 0.5, display: 'block', textAlign: 'center', fontSize: '0.65rem' }} noWrap>
                                                        {sc.imageComments}
                                                    </Typography>
                                                )}
                                            </Paper>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                </Paper>
            )}

            <Box component={"form"} onSubmit={handleSubmit(onSubmit)} ref={formRef} autoComplete="off">
                {/* Action Buttons Row */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                    {/* Template Selector */}
                    <TemplateSelector
                        onApplyTemplate={handleApplyTemplate}
                        examList={itemData?.examList}
                    />

                    {/* Report Mode Button */}
                    {hasStudyImages && (
                        <Tooltip title="Open full-screen view with DICOM viewer and report fields side by side">
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<FullscreenIcon />}
                                onClick={handleEnterReportMode}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1,
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderWidth: 2,
                                        bgcolor: 'primary.lighter',
                                    }
                                }}
                            >
                                Report Mode
                            </Button>
                        </Tooltip>
                    )}
                </Box>

                {/* OB Measurements Alert */}
                {showOBAlert && obMeasurements?.reportText && (
                    <Alert
                        severity="info"
                        sx={{ mb: 2 }}
                        action={
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button color="inherit" size="small" onClick={insertOBMeasurements}>
                                    Insert into Findings
                                </Button>
                                <Button color="inherit" size="small" onClick={() => setShowOBAlert(false)}>
                                    Dismiss
                                </Button>
                            </Box>
                        }
                    >
                        <strong>OB Measurements Available</strong> - Click "Insert into Findings" to add biometry and Doppler data to your report.
                    </Alert>
                )}

                <Grid container spacing={2}>
                    {/* Clinical Notes - no heading, free-form field */}
                    <Grid item lg={12} md={6} sm={6} xs={12} className='girdMarginBottom'>
                        <FormControl fullWidth className='inputfield'>
                            <Controller
                                name="diagnosticObjectives"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <DictationTextField
                                        {...field}
                                        placeholder='Clinical notes, history, or objectives...'
                                        rows={3}
                                    />
                                )}
                            />
                            {errors?.diagnosticObjectives?.message && <FormHelperText>{errors?.diagnosticObjectives?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item lg={12} md={6} sm={6} xs={12} className='girdMarginBottom'>
                        <FormControl fullWidth className='inputfield'>
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }} >Findings</FormLabel>
                            <Controller
                                name="findings"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <DictationTextField
                                        {...field}
                                        placeholder='Enter here or use microphone to dictate'
                                        rows={5}
                                    />
                                )}
                            />
                            {errors?.findings?.message && <FormHelperText>{errors?.findings?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item lg={12} md={6} sm={6} xs={12} className='girdMarginBottom'>
                        <FormControl fullWidth className='inputfield'>
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Radiographic Impression</FormLabel>
                            <Controller
                                name="radiographicImpression"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <DictationTextField
                                        {...field}
                                        placeholder='Enter here or use microphone to dictate'
                                        rows={5}
                                    />
                                )}
                            />
                            {errors?.radiographicImpression?.message && <FormHelperText>{errors?.radiographicImpression?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth className='selectDropdown' >
                            <FormLabel sx={{ marginBottom: "10px" }} >Appointer</FormLabel>
                            <SelectStaff setUserData={(id) => {
                                setValue("appointerId", id);
                                trigger("appointerId")
                            }} defaultId={getValues()?.appointerId} />
                            {errors?.appointerId?.message && <FormHelperText>{errors?.appointerId?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <FormLabel >Enter Appointer Signature Pin</FormLabel>
                            <OutlinedInput className="inputfield" name="appointerSignaturePin" type="password" size='small' autoComplete="new-password" {...register("appointerSignaturePin")} />
                            {errors?.appointerSignaturePin?.message && <FormHelperText>{errors?.appointerSignaturePin?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                </Grid>
                <Stack direction={"row"} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", justifyContent: "left", paddingTop: "20px", marginTop: "20px" }}>
                        <Button onClick={onBack} type="button" className='commonBtn' variant="contained" disabled={loading}>Back</Button>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", paddingTop: "20px", marginTop: "20px" }}>
                        { itemData?.reportData?.reportStatus === "completed"
                            ?
                            <Button type="submit" onClick={() => { setSaveType("exit") }} className='commonBtn' variant="contained" disabled={loading}>{loading ? "loading..." : "Update"}</Button>
                            :
                            <>
                                <Button sx={{ right: "20px" }} type="submit" onClick={() => { setSaveType("exit") }} className='commonBtn' variant="contained" disabled={loading}>{loading ? "loading..." : "Save"}</Button>
                                <Button type="submit" onClick={() => { setValue("reportStatus","completed"); setSaveType("exit") }} className='commonBtn' variant="contained" disabled={loading}>{loading ? "loading..." : "Submit"}</Button>
                            </>
                        }
                    </Box>
                </Stack>
            </Box>

            {/* OB Measurement Overlay */}
            {isOBExam && (
                <OBMeasurementOverlay
                    onCopyToReport={handleOBMeasurementsToReport}
                    lmpDate={itemData?.lmp ? new Date(itemData.lmp) : null}
                    examDate={itemData?.appoinmentDate ? new Date(itemData.appoinmentDate) : new Date()}
                    patientName={itemData?.seletedpatient ? `${itemData.seletedpatient.fName} ${itemData.seletedpatient.lName}` : null}
                    initialExpanded={false}
                />
            )}

            {/* PDF Popup Dialog */}
            <Dialog
                open={pdfDialogOpen}
                onClose={() => setPdfDialogOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { height: '90vh' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PictureAsPdfIcon sx={{ color: 'error.main' }} />
                        <Typography variant="subtitle1" fontWeight={600}>{pdfDialogTitle}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Open in new tab">
                            <IconButton size="small" onClick={() => window.open(pdfDialogUrl, '_blank')}>
                                <OpenInNewIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                            <IconButton size="small" onClick={() => {
                                if (pdfDialogInstanceId) handleDownloadPdf(pdfDialogInstanceId, pdfDialogTitle);
                            }}>
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={() => setPdfDialogOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.900' }}>
                    {pdfDialogType === 'image' ? (
                        <Box
                            component="img"
                            src={pdfDialogUrl}
                            alt={pdfDialogTitle}
                            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <iframe
                            src={pdfDialogUrl}
                            title={pdfDialogTitle}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}
